/**
 * Base API Client
 * Handles all backend API communication with authentication and error handling
 */

import { auth } from '../firebase';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';
const API_KEY = process.env.REACT_APP_API_KEY || 'gph-firebase-2026-x8k9mP4nQ7tJ2wL5vY6zA3bN1rH8sD9fG0cU';

/**
 * Base API client with authentication and error handling
 */
class APIClient {
  constructor(baseURL = API_BASE_URL, apiKey = API_KEY) {
    this.baseURL = baseURL;
    this.apiKey = apiKey;
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      'X-API-Key': apiKey
    };
  }

  /**
   * Make authenticated HTTP request
   * @param {string} endpoint - API endpoint (without base URL)
   * @param {Object} options - Fetch options
   * @returns {Promise<Object>} - Response data
   */
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const firebaseHeaders = {};
    const user = auth.currentUser;
    if (user) {
      try {
        firebaseHeaders.Authorization = `Bearer ${await user.getIdToken()}`;
      } catch (error) {
        console.warn('[API] Could not attach Firebase auth token:', error.message);
      }
    }
    const config = {
      ...options,
      headers: {
        ...this.defaultHeaders,
        ...firebaseHeaders,
        ...options.headers
      }
    };

    try {
      console.log(`[API] ${config.method || 'GET'} ${endpoint}`);
      
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        throw new APIError(
          data.detail || `HTTP ${response.status}`,
          response.status,
          data
        );
      }

      console.log(`[API] ✅ ${config.method || 'GET'} ${endpoint} - Success`);
      return data;

    } catch (error) {
      console.error(`[API] ❌ ${config.method || 'GET'} ${endpoint} - Error:`, error);
      
      if (error instanceof APIError) {
        throw error;
      }
      
      // Network or other errors
      throw new APIError(
        error.message || 'Network error',
        0,
        { originalError: error }
      );
    }
  }

  /**
   * GET request
   * @param {string} endpoint - API endpoint
   * @param {Object} params - URL query parameters
   * @returns {Promise<Object>} - Response data
   */
  async get(endpoint, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;
    
    return this.request(url, {
      method: 'GET'
    });
  }

  /**
   * POST request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body data
   * @returns {Promise<Object>} - Response data
   */
  async post(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /**
   * PATCH request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body data
   * @returns {Promise<Object>} - Response data
   */
  async patch(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }

  /**
   * DELETE request
   * @param {string} endpoint - API endpoint
   * @returns {Promise<Object>} - Response data
   */
  async delete(endpoint) {
    return this.request(endpoint, {
      method: 'DELETE'
    });
  }

  /**
   * Request with retry logic
   * @param {Function} requestFn - Function that makes the request
   * @param {number} maxRetries - Maximum number of retries
   * @param {number} delay - Delay between retries (ms)
   * @returns {Promise<Object>} - Response data
   */
  async requestWithRetry(requestFn, maxRetries = 3, delay = 1000) {
    let lastError;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await requestFn();
      } catch (error) {
        lastError = error;
        
        // Don't retry on authentication errors or client errors (4xx)
        if (error.status >= 400 && error.status < 500) {
          throw error;
        }
        
        if (attempt === maxRetries) {
          break;
        }
        
        console.log(`[API] Retry attempt ${attempt}/${maxRetries} after ${delay}ms...`);
        await this.sleep(delay);
      }
    }
    
    throw lastError;
  }

  /**
   * Sleep utility for retry delays
   * @param {number} ms - Milliseconds to sleep
   * @returns {Promise<void>}
   */
  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Check if API is reachable
   * @returns {Promise<boolean>} - True if API is healthy
   */
  async healthCheck() {
    try {
      // Try a simple endpoint
      await this.get('/scan-reports', { limit: 1 });
      return true;
    } catch (error) {
      console.warn('[API] Health check failed:', error.message);
      return false;
    }
  }
}

/**
 * Custom API Error class
 */
class APIError extends Error {
  constructor(message, status = 0, data = {}) {
    super(message);
    this.name = 'APIError';
    this.status = status;
    this.data = data;
  }

  /**
   * Check if error is due to authentication
   * @returns {boolean}
   */
  isAuthError() {
    return this.status === 401 || this.status === 403;
  }

  /**
   * Check if error is a server error
   * @returns {boolean}
   */
  isServerError() {
    return this.status >= 500;
  }

  /**
   * Check if error is a client error
   * @returns {boolean}
   */
  isClientError() {
    return this.status >= 400 && this.status < 500;
  }
}

// Create singleton API client instance
const apiClient = new APIClient();

export { apiClient, APIClient, APIError };
export default apiClient;
